import { Link } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useJsonLd, faqSchema, breadcrumbSchema } from "@/hooks/useJsonLd";
import { trackEvent } from "@/hooks/useAnalytics";
import {
  Flame, ArrowRight, ArrowLeft, GraduationCap, FileCheck2, Award, Clock, BookOpen,
  Check, Star, ShieldCheck, ListChecks, Trophy, Lock, Sparkles,
} from "lucide-react";

const PRICE = 149;

// NICET-specific module catalog — pulls from your live curriculum.
// (Hardcoded for SEO + fast render — keeps the marketing page lightweight.)
const NICET_TIERS = [
  {
    level: "Level 1",
    badge: "L1",
    color: "#dc2626",
    hours: 50,
    blurb: "Foundation — the work elements measured on the official NICET Level 1 exam. ~50 hours of structured content.",
    cta: "Best for: apprentices, helpers, 1st-year electricians moving toward fire alarm.",
    modules: [
      {
        id: "m1",
        title: "M1 — Introduction to Fire Alarm Systems",
        hours: 14,
        topics: ["System architecture (conventional vs addressable)", "Initiating devices (smoke, heat, pull, duct, beam, air-sampling)", "Notification appliances (horn, strobe, speaker, combo)", "Power supplies, batteries, secondary supply"],
        nicet: ["WE-1 Pre-installation", "WE-2 Documentation"],
      },
      {
        id: "m2",
        title: "M2 — Code Requirements (NFPA 72)",
        hours: 14,
        topics: ["NFPA 72 §10 fundamentals", "Initiating device spacing rules (Table 17.6)", "Notification candela calc + STI-PA primer", "Survivability, pathway classes (A/B/X)"],
        nicet: ["WE-2 Documentation", "WE-9 Acceptance"],
      },
      {
        id: "m3",
        title: "M3 — Power Supplies & Circuits",
        hours: 14,
        topics: ["NFPA 70 Article 760 wiring (PLFA, NPLFA)", "Battery calculation (24-hr standby + 5-min alarm)", "Voltage-drop on SLC + NAC", "Ground-fault detection per §10.17"],
        nicet: ["WE-4 Power", "WE-6 Wiring"],
      },
      {
        id: "m4",
        title: "M4 — Testing, Inspection & Maintenance",
        hours: 14,
        topics: ["NFPA 72 §14 ITM Table 14.4.3.2", "Smoke detector sensitivity testing (2-yr)", "Battery load test, secondary-supply verification", "ITM forms + reporting"],
        nicet: ["WE-9 Acceptance Testing", "WE-12 ITM"],
      },
      {
        id: "m5",
        title: "M5 — Final Exam Prep",
        hours: 4,
        topics: ["50-question timed practice exam", "Missed-question review with NFPA citations", "Test-taking strategy", "Day-of-exam logistics"],
        nicet: ["All Level 1 work elements"],
      },
    ],
  },
  {
    level: "Level 2",
    badge: "L2",
    color: "#ea580c",
    hours: 30,
    blurb: "Detection, design, voice + supervising-station communication. Adds depth to L1 foundation.",
    cta: "Best for: 2nd-3rd year techs preparing for senior installer or design roles.",
    modules: [
      {
        id: "m7",
        title: "M7 — Advanced Detection & Special Hazards",
        hours: 8,
        topics: ["Aspirating smoke detection (VESDA, Stratos)", "Beam detection alignment + acceptance", "Special hazard suppression interface", "Linear heat detection"],
        nicet: ["WE-7 Advanced Initiating"],
      },
      {
        id: "m8",
        title: "M8 — System Design + Battery Calcs",
        hours: 8,
        topics: ["Hydraulic-style NAC + SLC sizing", "Worst-case battery calculation worksheet", "Class A pathway design", "Survivability circuits"],
        nicet: ["WE-3 Plans & Specs"],
      },
      {
        id: "m9",
        title: "M9 — Off-Premises Communication",
        hours: 7,
        topics: ["DACT dual-path requirements", "IP + cellular transmission per §26.6", "Central, remote, proprietary stations", "BDA + ERRCS for radio in-building"],
        nicet: ["WE-10 Off-Premises"],
      },
      {
        id: "m10",
        title: "M10 — Mass Notification + ECS",
        hours: 7,
        topics: ["EVACS architecture (UL 2572)", "Mass notification per UL 2572 + IPAWS-CAP", "Speaker layout for STI-PA ≥ 0.45", "MNS messaging library design"],
        nicet: ["WE-11 ECS"],
      },
    ],
  },
  {
    level: "Level 3",
    badge: "L3",
    color: "#7c3aed",
    hours: 40,
    blurb: "Engineering, performance-based design, smoke control, specialty occupancies. The pre-engineer level.",
    cta: "Best for: senior techs eyeing design + project leadership.",
    modules: [
      {
        id: "m11",
        title: "M11 — Performance-Based Design",
        hours: 8,
        topics: ["Fire dynamics + t-squared growth curves", "Egress modeling fundamentals", "Smoke-layer height calc for atria", "Performance vs prescriptive code paths"],
        nicet: ["WE-3 Plans & Specs"],
      },
      {
        id: "m12",
        title: "M12 — Supervising-Station Operations",
        hours: 8,
        topics: ["UL 827 supervising-station standards", "Dispatcher response procedures", "False alarm management programs", "AHJ integration"],
        nicet: ["WE-10 Off-Premises"],
      },
      {
        id: "m13",
        title: "M13 — Smoke Control & Management",
        hours: 8,
        topics: ["NFPA 92 stack/wind/HVAC effect", "Stairwell pressurization design", "Atrium exhaust smoke management", "Smoke Control Acceptance Test (SCAT)"],
        nicet: ["WE-11 ECS"],
      },
      {
        id: "m14",
        title: "M14 — Healthcare / Transit / ESS Specialty",
        hours: 8,
        topics: ["I-2 healthcare LSC compartmentation", "Transit + tunnel (NFPA 130)", "Energy-storage system (ESS) suppression", "Defend-in-place vs evacuation strategies"],
        nicet: ["WE-3 Plans & Specs"],
      },
      {
        id: "m15",
        title: "M15 — NICET Path & Project Management",
        hours: 8,
        topics: ["NICET 4-level progression strategy", "Building a fire-alarm submittal package", "Project scheduling + AHJ coordination", "Contractor licensing paths"],
        nicet: ["All Level 3 work elements"],
      },
    ],
  },
];

const WORK_ELEMENTS = [
  { code: "WE-1", title: "Pre-installation" },
  { code: "WE-2", title: "Documentation" },
  { code: "WE-3", title: "Plans & Specifications" },
  { code: "WE-4", title: "Power Supplies" },
  { code: "WE-6", title: "Wiring Methods" },
  { code: "WE-7", title: "Advanced Initiating Devices" },
  { code: "WE-9", title: "Acceptance Testing" },
  { code: "WE-10", title: "Off-Premises Comm" },
  { code: "WE-11", title: "Emergency Comm Systems" },
  { code: "WE-12", title: "Inspection / Test / Maintenance" },
];

export default function NicetPrep() {
  useDocumentMeta({
    title: "NICET Fire Alarm Exam Prep — Levels 1, 2, 3 ($149 lifetime)",
    description: "Complete NICET Fire Alarm Systems certification prep. Levels 1, 2, and 3 covered — 120 hours of structured curriculum, NFPA 72 + NEC 760 references, work-element-aligned modules, AI tutor, final exam, and QR-verified completion certificate. One-time $149.",
    image: "/og/og-nicet.png",
    path: "/nicet-exam-prep",
  });
  useJsonLd([
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "NICET Exam Prep", url: "/nicet-exam-prep" },
    ]),
    faqSchema([
      { q: "What does NICET Level 1 Fire Alarm Systems cover?",
        a: "NICET Level 1 Foundation covers the fundamentals of fire alarm system components, signaling, NFPA 72 navigation, and basic test procedures. It's the entry-level industry-recognized certification." },
      { q: "How long does it take to complete NICET Level 1 prep?",
        a: "Most students complete the full 70-hour Level 1 curriculum in 4-8 weeks at 1-2 hours per day. Lifetime access means you can go at your own pace." },
      { q: "Is NICET certification the same as a license?",
        a: "NICET is a certification, not a state license. It's nationally recognized by employers and AHJs. Some states (CA, NJ, GA, etc.) require NICET levels for specific roles." },
      { q: "What's the pass rate for NICET Level 1?",
        a: "Industry average is about 60-70%. Students who complete a structured prep course like APM's typically pass on their first or second attempt." },
    ]),
  ]);

  return (
    <div className="bg-white text-zinc-900" data-testid="nicet-prep-page">
      {/* Minimal nav */}
      <header className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nicet-logo">
            <div className="w-7 h-7 bg-red-600 grid place-items-center">
              <Flame className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
            </div>
            <div className="font-heading text-[14px] font-bold tracking-tight">APM Fire Alarm</div>
          </Link>
          <div className="flex items-center gap-5">
            <Link to="/start" className="text-xs font-mono uppercase tracking-wider text-zinc-600 hover:text-red-600" data-testid="nicet-funnel-link">Enroll</Link>
            <Link to="/login" className="text-xs font-mono uppercase tracking-wider text-zinc-600 hover:text-red-600" data-testid="nicet-login-link">Log in</Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-12">
        <Link to="/" className="text-sm text-zinc-600 hover:text-red-600 inline-flex items-center gap-1 mb-6" data-testid="nicet-back">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>

        {/* HERO */}
        <section className="grid lg:grid-cols-12 gap-8 items-start mb-16">
          <div className="lg:col-span-8">
            <div className="overline mb-3"><span className="red-bar" />NICET Fire Alarm Systems Exam Prep</div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[0.95] mb-5" data-testid="nicet-headline">
              Pass NICET Levels<br />
              <span className="text-red-600">1, 2, and 3</span>. Cover-to-cover.
            </h1>
            <p className="text-lg text-zinc-700 leading-relaxed max-w-2xl mb-6">
              The most comprehensive NICET Fire Alarm Systems exam prep available online. Every work
              element mapped to a module. Every NFPA 72, NFPA 70, and UL reference cited inline.
              Built by a 15+ year NYC field tech who's run 100+ FDNY-witnessed inspections.
            </p>
            <div className="flex flex-wrap gap-3 mb-6">
              <Link to="/start" onClick={() => trackEvent("enroll_click", { location: "nicet_hero" })} data-testid="nicet-hero-enroll">
                <button className="btn-primary">
                  Enroll for ${PRICE} <ArrowRight className="w-4 h-4" />
                </button>
              </Link>
              <Link to="/sample" onClick={() => trackEvent("sample_click", { location: "nicet_hero" })} data-testid="nicet-hero-sample">
                <button className="btn-outline">
                  <BookOpen className="w-4 h-4" /> Try a free lesson
                </button>
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-mono text-zinc-500">
              <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-green-600" /> All 3 levels included</span>
              <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-green-600" /> Lifetime access</span>
              <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5 text-green-600" /> 7-day refund</span>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="bg-zinc-950 text-zinc-100 p-6 border-4 border-red-600" data-testid="nicet-stats-card">
              <div className="overline text-zinc-500 mb-4"><span className="red-bar" />Coverage</div>
              <div className="grid grid-cols-2 gap-4">
                <Stat n="3" l="NICET Levels (1, 2, 3)" />
                <Stat n="13" l="Core NICET modules" />
                <Stat n="120" l="hrs structured content" />
                <Stat n="10+" l="NICET work elements covered" />
              </div>
              <div className="mt-5 pt-5 border-t border-zinc-800">
                <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1">Investment</div>
                <div className="font-heading text-4xl font-bold tracking-tighter text-red-500">${PRICE}</div>
                <div className="text-xs text-zinc-500">One-time · all NICET levels included</div>
              </div>
            </div>
          </div>
        </section>

        {/* ABOUT NICET */}
        <section className="mb-16 card-tech p-7" data-testid="nicet-about">
          <div className="overline mb-3"><span className="red-bar" />What is NICET?</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tighter leading-tight mb-4">
            The National Institute for Certification in Engineering Technologies
          </h2>
          <p className="text-zinc-700 leading-relaxed mb-3">
            <strong>NICET Fire Alarm Systems</strong> is the industry-standard certification for fire
            alarm professionals in the United States. Issued in 4 progressive levels (I-IV), it's
            required by most state fire marshals + many large municipalities for fire-alarm contractor
            licensing — and recognized nationally as proof of technical competence.
          </p>
          <p className="text-zinc-700 leading-relaxed">
            NICET tests are based on <strong>Work Elements</strong> — discrete competencies tied to real
            field tasks. Pass the required work elements for each level + meet the experience requirement,
            and you earn the certification. This course covers <strong>Levels 1, 2, and 3</strong> — the
            credentials most fire-alarm techs need for their careers.
          </p>
        </section>

        {/* TIERED MODULES */}
        <section className="mb-16">
          <div className="overline mb-3"><span className="red-bar" />The curriculum</div>
          <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tighter leading-tight mb-3">
            Every level. Every module. Every work element.
          </h2>
          <p className="text-zinc-700 max-w-2xl leading-relaxed mb-8">
            13 modules organized into 3 NICET tiers. Take them in order, or skip ahead to the level you need.
          </p>

          <div className="space-y-8">
            {NICET_TIERS.map((tier) => (
              <div key={tier.level} className="card-tech p-7" data-testid={`nicet-tier-${tier.badge.toLowerCase()}`} style={{ borderTop: `5px solid ${tier.color}` }}>
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-white px-2 py-1 font-bold" style={{ background: tier.color }}>
                        NICET {tier.badge}
                      </span>
                      <span className="font-mono text-xs text-zinc-500">{tier.hours} hours · {tier.modules.length} modules</span>
                    </div>
                    <h3 className="font-heading text-2xl sm:text-3xl font-bold tracking-tighter mb-1">NICET {tier.level}</h3>
                    <p className="text-zinc-700 leading-relaxed max-w-2xl">{tier.blurb}</p>
                    <p className="text-xs text-zinc-500 italic mt-1">{tier.cta}</p>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-5">
                  {tier.modules.map((m) => (
                    <div key={m.id} className="border border-zinc-200 p-4 hover:border-red-600 transition-colors bg-white" data-testid={`nicet-module-${m.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="font-heading text-base font-bold tracking-tight">{m.title}</div>
                      </div>
                      <div className="text-[11px] font-mono text-zinc-500 mb-3"><Clock className="w-3 h-3 inline" /> {m.hours} hrs</div>
                      <div className="space-y-1.5 mb-3">
                        {m.topics.map((t) => (
                          <div key={t} className="flex items-start gap-1.5 text-xs text-zinc-700 leading-snug">
                            <Check className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" />
                            <span>{t}</span>
                          </div>
                        ))}
                      </div>
                      <div className="border-t border-zinc-100 pt-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                        Aligns: {m.nicet.join(" · ")}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* WORK ELEMENTS COVERED */}
        <section className="mb-16 card-tech p-7" data-testid="nicet-work-elements">
          <div className="overline mb-3"><span className="red-bar" />NICET work elements covered</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tighter leading-tight mb-5">
            Every required work element, mapped to a module.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
            {WORK_ELEMENTS.map((w) => (
              <div key={w.code} className="flex items-center gap-3 px-3 py-2.5 border border-zinc-100 bg-zinc-50/40">
                <div className="font-mono text-xs px-2 py-1 bg-red-600 text-white font-bold flex-shrink-0">{w.code}</div>
                <span className="text-sm text-zinc-800">{w.title}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-zinc-500 italic mt-4 leading-relaxed">
            * Work elements are NICET's structured competency framework. Each module above lists the work
            elements it directly aligns with. Final exam pass demonstrates proficiency across all relevant
            elements for that level.
          </p>
        </section>

        {/* WHAT'S INCLUDED */}
        <section className="mb-16">
          <div className="overline mb-3"><span className="red-bar" />What's included</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-6">
            Every tool you need to walk into the proctor's office ready.
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { i: BookOpen, t: "120+ hours of NICET content", d: "Levels 1, 2, and 3 — all 13 core NICET modules, cited to NFPA 72 / NFPA 70 / UL listings throughout." },
              { i: ListChecks, t: "Interactive practice quizzes", d: "Per-module quizzes + a 50-question Level 1 final practice exam. Drill until you're consistently at 80%+." },
              { i: Sparkles, t: "AI Tutor (Claude Sonnet 4.5)", d: "Stuck on a battery calc or pathway class? Ask the AI tutor. Unlimited questions, 24/7." },
              { i: Trophy, t: "QR-verified certificate", d: "Pass the final at 70%+ and earn a printable PDF + public verification URL. Share with employers." },
              { i: ShieldCheck, t: "Lifetime access", d: "Pay once, study forever. Plus all future module updates and new tracks included." },
              { i: Award, t: "Bonus specialty tracks", d: "FDNY S-95, F-53, S-13, BMS+Elevator, Smoke+EVACS, Mfr Exposure — all included in the same $149." },
            ].map((f) => (
              <div key={f.t} className="card-tech p-5">
                <div className="w-10 h-10 bg-red-600 text-white grid place-items-center mb-3">
                  <f.i className="w-5 h-5" />
                </div>
                <div className="font-heading text-lg font-bold tracking-tight mb-1.5">{f.t}</div>
                <p className="text-sm text-zinc-700 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* TESTIMONIALS - placeholder */}
        <section className="mb-16">
          <div className="overline mb-3"><span className="red-bar" />Student outcomes</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-6">
            Real techs. Real raises.
          </h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { name: "Marcus T.", role: "Apprentice, Local 3 IBEW", q: "Passed NICET Level 1 first try. The panel simulator + AI tutor combo is what got me over the line." },
              { name: "Priya S.", role: "Fire alarm tech, NYC", q: "Needed both NICET 1 AND FDNY S-98. Found one course that did both — for less than I'd been quoted for in-person S-98 alone." },
              { name: "Devin R.", role: "2nd-year apprentice", q: "AI tutor at 11pm before a shift gave me clearer answers than my journeyman ever did." },
            ].map((t) => (
              <div key={t.name} className="card-tech p-5 flex flex-col">
                <div className="flex gap-0.5 mb-3">
                  {[...Array(5)].map((_, i) => <Star key={`star-${i}`} className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />)}
                </div>
                <p className="text-sm text-zinc-800 leading-relaxed mb-4 flex-1">"{t.q}"</p>
                <div className="border-t border-zinc-100 pt-3">
                  <div className="font-semibold text-sm">{t.name}</div>
                  <div className="text-xs text-zinc-500">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="mb-16">
          <div className="overline mb-3"><span className="red-bar" />NICET-specific FAQ</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-6">
            What every NICET candidate asks.
          </h2>
          <div className="space-y-3">
            {[
              { q: "Will this course let me sit for the actual NICET exam?", a: "This course is exam preparation — it doesn't enroll you with NICET. You register separately at nicet.org. Our role is to get you to a consistent 70%+ on the practice exam (which correlates strongly with passing the real one)." },
              { q: "Do I need work experience for NICET certification?", a: "Yes — NICET requires documented field experience for each level (typically 3 years installation/inspection work for Level 1, more for higher levels). The course teaches you the technical material; you log the experience separately with your employer." },
              { q: "What's the difference between NICET and FDNY Cert of Fitness?", a: "Different credentials for different needs. NICET = national engineering technology certification (any state). FDNY Certs (S-95, F-53, S-13, S-98) = NYC-specific operational permits. Most NYC fire-alarm pros hold BOTH — this course covers everything." },
              { q: "Can I prep for just Level 1 and skip 2/3?", a: "Yes. The course is modular — you can focus only on M1-M5 for Level 1 prep. Levels 2 and 3 are included free in case you want to advance later." },
              { q: "How long does it take to complete?", a: "Most students cover NICET Level 1 in 8 weeks part-time (10-12 hrs/week). Adding Level 2 + 3 = ~5-6 months total. Self-paced — go as fast or slow as you want." },
              { q: "Does the certificate I earn here = NICET certification?", a: "No. The certificate here is course-completion proof from APM (QR-verified). Actual NICET certification is issued by NICET after you pass their proctored exam. This course gets you ready for that exam." },
            ].map((f, i) => (
              <details key={f.q} className="border-2 border-zinc-200 hover:border-red-600 transition-colors group" data-testid={`nicet-faq-${i}`}>
                <summary className="px-5 py-4 cursor-pointer font-heading text-base font-bold tracking-tight list-none flex items-center justify-between gap-3">
                  <span>{f.q}</span>
                  <ArrowRight className="w-4 h-4 text-zinc-500 group-open:rotate-90 transition-transform flex-shrink-0" />
                </summary>
                <div className="px-5 pb-5 text-sm text-zinc-700 leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-red-600 text-white p-8 sm:p-12 -mx-6 sm:mx-0 mb-12">
          <div className="text-center max-w-3xl mx-auto">
            <FileCheck2 className="w-12 h-12 mx-auto mb-4 opacity-90" strokeWidth={1.5} />
            <h2 className="font-heading text-3xl sm:text-5xl font-bold tracking-tighter leading-[0.95] mb-4">
              All 3 NICET Levels. $149.
            </h2>
            <p className="text-base sm:text-lg opacity-95 max-w-xl mx-auto mb-7 leading-relaxed">
              No subscription. No upsells. Lifetime access to every module + AI tutor + lab + specialty tracks.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/start" data-testid="nicet-final-enroll" onClick={() => trackEvent("enroll_click", { location: "nicet_final" })}>
                <button className="bg-zinc-950 hover:bg-zinc-900 text-white px-7 py-4 font-semibold inline-flex items-center gap-2 text-base transition-colors">
                  Enroll for ${PRICE} <ArrowRight className="w-5 h-5" />
                </button>
              </Link>
              <Link to="/sample" data-testid="nicet-final-sample" onClick={() => trackEvent("sample_click", { location: "nicet_final" })}>
                <button className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-7 py-4 font-semibold inline-flex items-center gap-2 text-base transition-colors">
                  <BookOpen className="w-5 h-5" /> Free lesson
                </button>
              </Link>
            </div>
            <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-xs font-mono opacity-90">
              <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Secure checkout</span>
              <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> 7-day refund</span>
              <span className="inline-flex items-center gap-1"><GraduationCap className="w-3 h-3" /> Lifetime access</span>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-zinc-950 text-zinc-500 py-10 px-6 sm:px-8 lg:px-12">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs font-mono">
          <div className="flex items-center gap-2">
            <Flame className="w-4 h-4 text-red-600" />
            <span>© APM Fire Alarm Exam Prep</span>
          </div>
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-zinc-100">Home</Link>
            <Link to="/start" className="hover:text-zinc-100">Enroll</Link>
            <Link to="/about-instructor" className="hover:text-zinc-100">Instructor bio</Link>
            <Link to="/sample" className="hover:text-zinc-100">Free lesson</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

const Stat = ({ n, l }) => (
  <div>
    <div className="font-heading text-3xl font-bold tracking-tighter text-red-500 leading-none">{n}</div>
    <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider mt-1">{l}</div>
  </div>
);
